// Note: This file is a copy of the FormField component from the IFL library.
import type { BoxStyles } from '../../../../mads/Box';

import { Label } from './Label';
import { Box, Flex, Text } from '../../../../mads';
import { Error } from '../../../../mads/icons';

import { colors } from '@indeed/ifl-tokens';
import React, { type ReactElement, type ReactNode } from 'react';
import { StyleSheet } from 'react-native';

export type FormFieldSize = 'md' | 'sm';

export interface FormFieldProps {
    /**
     * The input controls to render for this field.
     */
    children?: ReactNode;
    /**
     * identifies that the field is disabled.
     */
    disabled?: boolean;
    /**
     * The error text for the field when invalid is `true` (renders below the children).
     */
    errorText?: string;
    /**
     * The helper text for the field (renders below the label).
     */
    helperText?: string;
    /**
     * Styles the field content to indicate that it is invalid.
     */
    invalid?: boolean;
    /**
     * The label text for the field.
     */
    label?: string;
    /**
     * The label text style for the field.
     */
    labelSx?: BoxStyles;
    /**
     * Styles the label as required by displaying an asterisk.
     */
    showRequiredIndicator?: boolean;
    /**
     * Change the style type of the field. Choose between `sm` or `md`.
     */
    size?: FormFieldSize;
}

/**
 * @see https://experience.indeed.design/components/ids/form-field
 */
export function FormField({
    children,
    disabled,
    errorText,
    helperText,
    invalid,
    label,
    labelSx,
    showRequiredIndicator = false,
    size = 'md',
}: FormFieldProps): ReactElement {
    const hasError = !!(invalid && errorText);
    return (
        <Box>
            {label && (
                <Box sx={[styles.label, labelSx] as BoxStyles}>
                    <Label
                        showRequiredIndicator={showRequiredIndicator}
                        disabled={disabled}
                        invalid={invalid}
                        size={size}
                    >
                        {label}
                    </Label>
                </Box>
            )}
            {helperText && (
                <Box sx={{ marginBottom: 4 }}>
                    <Text
                        level={2}
                        sx={[
                            { color: colors.foreground['[base]'] },
                            disabled ? { opacity: 0.4 } : {},
                        ]}
                    >
                        {helperText}
                    </Text>
                </Box>
            )}
            {children}
            {hasError && (
                <Box>
                    <Flex
                        sx={[
                            { flexDirection: 'row', marginTop: 8 },
                            disabled ? { opacity: 0.4 } : {},
                        ]}
                    >
                        <Box>
                            <Error
                                size="xs"
                                color={colors.foreground.status.critical['[base]']}
                                aria-hidden
                            />
                        </Box>
                        <Box sx={{ paddingHorizontal: 8 }}>
                            <Text
                                level={2}
                                sx={{ color: colors.foreground.status.critical['[base]'] }}
                            >
                                {errorText}
                            </Text>
                        </Box>
                    </Flex>
                </Box>
            )}
        </Box>
    );
}

const styles = StyleSheet.create({
    label: {
        marginBottom: 4,
    },
});

FormField.displayName = 'FormField';
